import React, { useState, useCallback, useMemo } from "react";
import { Button as BtnPrimary } from "@talentbait/shared-components";
import { useTranslation } from "react-i18next";
import logger from "../../../../utils/LoggerFrontend";
import Actions from "../../../../Actions";
import { validateEmail } from "../../../../utils/Helpers";
import { CreateButtonWrapper, Header, ModalContentWrapper } from "./InvoiceAddressModal.styled";
import { CampaignCreationStore, withRefluxStore } from "../../../../Stores";
import {
  ADDRESS_MAX_LENGTH,
  CITY_MAX_LENGTH,
  COMPANY_NAME_MAX_LENGTH,
  getCountries,
  POSTCODE_LENGTH
} from "./utils";
import { SelectInput, TextInput } from "../../../../Components/Form";
import { Group } from "../../../../Components/Layout";

const CreateInvoiceAddressModal = ({
  creatingDebitoorCompany,
  companyName,
  descriptionText,
  organizationId,
  onCancel = () => {}
}) => {
  const { t } = useTranslation();

  const [name, setName] = useState(companyName);
  const changeName = useCallback((e) => setName(e.target.value), []);

  const [address, setAddress] = useState({
    street: "",
    zip: "",
    city: ""
  });

  const changeAddress = useCallback((key) => {
    return (e) => {
      const newValue = e.target.value;
      setAddress((oldAddress) => ({
        ...oldAddress,
        [key]: newValue
      }));
    };
  }, []);

  const [email, setEmail] = useState("");
  const changeEmail = useCallback((e) => setEmail(e.target.value), []);

  const [country, setCountry] = useState("DE");
  const changeCountry = useCallback(({ value }) => setCountry(value), []);

  const selectedCountryOption = useMemo(
    () => getCountries().find(({ value }) => value === country),
    [country]
  );

  const createInvoiceAddress = useCallback(() => {
    logger.log("CampaignCreate:CreateInvoiceAddressModal:createInvoiceAddress", "called", {
      name,
      email,
      address
    });

    Actions.createDebitoorCompany({
      companyNameValue: name,
      addressValue: address,
      emailValue: email,
      countryCode: country,
      organizationId
    });

    Actions.loadOrganization(organizationId);
  }, [name, address, email, country, organizationId]);

  const zipValid = useMemo(
    () => address.zip && address.zip.length <= POSTCODE_LENGTH,
    [address.zip]
  );
  const emailValid = useMemo(() => email && validateEmail(email), [email]);

  const disabled = useMemo(
    () =>
      !name ||
      !address.street ||
      !address.zip ||
      !address.city ||
      creatingDebitoorCompany ||
      !emailValid,
    [address, name, creatingDebitoorCompany, emailValid]
  );

  return (
    <ModalContentWrapper>
      <Header>
        {descriptionText || t("Please enter your billing address to proceed with the booking")}
      </Header>

      <Group direction="column" gap="15px">
        <TextInput
          label={t("Company")}
          placeholder={t("Company")}
          value={name}
          onChange={changeName}
          maxLen={COMPANY_NAME_MAX_LENGTH}
          isRequired={true}
          width="100%"
        />

        <TextInput
          label={t("Email")}
          placeholder={t("Email")}
          value={email}
          onChange={changeEmail}
          maxLen={COMPANY_NAME_MAX_LENGTH}
          isRequired={true}
          invalid={!emailValid}
          width="100%"
        />

        <TextInput
          label={t("Street")}
          placeholder={t("Street")}
          value={address.street}
          onChange={changeAddress("street")}
          maxLen={ADDRESS_MAX_LENGTH}
          isRequired={true}
          invalid={!address.street}
          width="100%"
        />

        <Group gap="10px">
          <TextInput
            label={t("Postcode")}
            placeholder={t("Postcode")}
            value={address.zip}
            onChange={changeAddress("zip")}
            maxLen={POSTCODE_LENGTH}
            isRequired={true}
            flex="3 1 0"
            invalid={!zipValid}
          />

          <TextInput
            label={t("City")}
            placeholder={t("City")}
            value={address.city}
            onChange={changeAddress("city")}
            maxLen={CITY_MAX_LENGTH}
            isRequired={true}
            flex="7 1 0"
            invalid={!address.city}
          />
        </Group>

        <SelectInput
          label={t("Country")}
          placeholder={t("Country")}
          value={selectedCountryOption}
          onChange={changeCountry}
          options={getCountries()}
          width="100%"
        />
      </Group>

      <CreateButtonWrapper>
        <BtnPrimary
          type="tertiary"
          variant="outline"
          onClick={onCancel}
          style={{ marginRight: "10px" }}
        >
          {t("Cancel")}
        </BtnPrimary>

        <BtnPrimary type="secondary" onClick={createInvoiceAddress} disabled={disabled}>
          {t("Create billing address")}
        </BtnPrimary>
      </CreateButtonWrapper>
    </ModalContentWrapper>
  );
};

export default withRefluxStore(
  CreateInvoiceAddressModal,
  [CampaignCreationStore],
  ["creatingDebitoorCompany", "companyName"]
);
